import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from '@com/core/common/translations/i18n';

type TBannerTimerProps = {
    displayUnitsBottom?: boolean;
    isSlider?: boolean;
};

const BannerTimerWrapper = styled.div<TBannerTimerProps>`
    direction: ltr;
    display: flex;

    :global(html[dir='rtl']) & {
        margin-right: 0px;
        margin-top: -4px;

        ${(props): string => (props.isSlider ? 'margin-top: 15px;' : '')}
    }
    ${(props): string => (props.displayUnitsBottom ? 'flex-direction: row;' : '')}
`;

const TimeUnit = styled.span`
    font-size: 10px;
    padding: 0 4px;
`;

const TimerDigit = styled.div<{ dotsCtr?: boolean }>`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin: 0px;
    ${(props: { dotsCtr?: boolean }): string => (props.dotsCtr ? 'padding: 0px 3px' : '')}
`;

const DealTimer = styled.span<{ timerFontSize?: string }>`
    font-size: ${(props: { timerFontSize?: string }): string => props.timerFontSize || '16'}px;
`;

const DigitCtr = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: flex-start;
`;

type TProps = {
    endTime: string;
    disableDays?: boolean;
    displayUnitsBottom?: boolean;
    isSlider?: boolean;
    timerFontSize?: string;
};

function useTimer(endTime: string, disableDays = false): [number, number, number, number] {
    const [days, setDays] = useState<number>(0);
    const [hours, setHours] = useState<number>(0);
    const [minutes, setMinutes] = useState<number>(0);
    const [seconds, setSeconds] = useState<number>(0);

    useEffect(() => {
        const timer = (interval: number): void => {
            const countDownDate = new Date(endTime).getTime();
            const now = new Date().getTime();
            const distance = countDownDate - now;
            const newDays = Math.floor(distance / (1000 * 60 * 60 * 24));
            let newHours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

            // If the days are disabled, add them to hours
            if (disableDays) {
                newHours = newDays * 24 + newHours;
            }
            const newMinutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
            const newSeconds = Math.floor((distance % (1000 * 60)) / 1000);
            if (distance < 0) {
                if (interval) {
                    clearInterval(interval);
                }
                setDays(0);
                setHours(0);
                setMinutes(0);
                setSeconds(0);
            } else {
                setDays(newDays);
                setHours(newHours);
                setMinutes(newMinutes);
                setSeconds(newSeconds);
            }
        };
        const interval = setInterval(timer, 1000);

        return (): void => clearInterval(interval);
    }, [disableDays, endTime]);

    return [days, hours, minutes, seconds];
}

const BannerTimer = ({ disableDays, displayUnitsBottom, endTime, isSlider, timerFontSize }: TProps): JSX.Element => {
    const { t } = useTranslation('common');
    const [days, hours, minutes, seconds] = useTimer(endTime, disableDays);
    const hourSliceOffset = (!!hours || hours === 0) && hours.toString().length > 2 ? -hours.toString().length : -2;

    return (
        <BannerTimerWrapper displayUnitsBottom={displayUnitsBottom} isSlider={isSlider}>
            {!!days && days > 0 && !disableDays && (
                <div>
                    {!displayUnitsBottom ? (
                        <TimerDigit>
                            <DealTimer timerFontSize={timerFontSize}>
                                {days}
                                D&nbsp;:&nbsp;
                            </DealTimer>
                        </TimerDigit>
                    ) : (
                        <DigitCtr>
                            <TimerDigit>
                                <DealTimer timerFontSize={timerFontSize}>{days}</DealTimer>
                                <TimeUnit>{days === 1 ? t('_banner_timer-day') : t('_banner_timer-days')}</TimeUnit>
                            </TimerDigit>
                            <TimerDigit dotsCtr>
                                <DealTimer timerFontSize={timerFontSize}>:</DealTimer>
                            </TimerDigit>
                        </DigitCtr>
                    )}
                </div>
            )}
            {(!!hours || hours === 0) && (
                <div>
                    {!displayUnitsBottom ? (
                        <TimerDigit>
                            <DealTimer timerFontSize={timerFontSize}>
                                {`0${hours}`.slice(hourSliceOffset)}
                                H&nbsp;:&nbsp;
                            </DealTimer>
                        </TimerDigit>
                    ) : (
                        <DigitCtr>
                            <TimerDigit>
                                <DealTimer timerFontSize={timerFontSize}>
                                    {`0${hours}`.slice(hourSliceOffset)}
                                </DealTimer>
                                <TimeUnit>{hours === 1 ? t('_banner_timer-hour') : t('_banner_timer-hours')}</TimeUnit>
                            </TimerDigit>
                            <TimerDigit dotsCtr>
                                <DealTimer timerFontSize={timerFontSize}>:</DealTimer>
                            </TimerDigit>
                        </DigitCtr>
                    )}
                </div>
            )}
            {(!!minutes || minutes === 0) && (
                <div>
                    {!displayUnitsBottom ? (
                        <TimerDigit>
                            <DealTimer timerFontSize={timerFontSize}>
                                {`0${minutes}`.slice(-2)}
                                M&nbsp;:&nbsp;
                            </DealTimer>
                        </TimerDigit>
                    ) : (
                        <DigitCtr>
                            <TimerDigit>
                                <DealTimer timerFontSize={timerFontSize}>{`0${minutes}`.slice(-2)}</DealTimer>
                                <TimeUnit>
                                    {minutes === 1 ? t('_banner_timer-minute') : t('_banner_timer-minutes')}
                                </TimeUnit>
                            </TimerDigit>
                            <TimerDigit dotsCtr>
                                <DealTimer timerFontSize={timerFontSize}>:</DealTimer>
                            </TimerDigit>
                        </DigitCtr>
                    )}
                </div>
            )}
            {(!!seconds || seconds === 0) && (
                <span>
                    {' '}
                    {!displayUnitsBottom ? (
                        <TimerDigit>
                            <DealTimer timerFontSize={timerFontSize}>{`0${seconds}`.slice(-2)}S</DealTimer>
                        </TimerDigit>
                    ) : (
                        <TimerDigit>
                            <DealTimer timerFontSize={timerFontSize}>{`0${seconds}`.slice(-2)}</DealTimer>
                            <TimeUnit>
                                {seconds === 1 ? t('_banner_timer-second') : t('_banner_timer-seconds')}
                            </TimeUnit>
                        </TimerDigit>
                    )}
                </span>
            )}
        </BannerTimerWrapper>
    );
};

export default BannerTimer;
